import { useEffect, useState, useMemo } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useFiscalStore } from "@/store/fiscal-store";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { fmtDate, fmtBRL, fmtNum } from "@/lib/format";
import { 
  ShieldCheck, ShieldOff, UserCog, Trash2, Plus, RefreshCw, Copy, KeyRound, Users,
  CreditCard, TrendingUp, Sparkles, Settings, Globe, Activity, Percent, Save, Info, Scale, Database,
  Zap, Layers, Search, Filter, Calculator, FileCheck, FileCode, AlertTriangle, Download,
  ToggleLeft, ToggleRight, BookOpen, Edit2, CheckCircle2, UserPlus, Loader2
} from "lucide-react";
import { createUserAdmin } from "@/lib/nfe-backend";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import type { AuditSeverity, AuditCategory } from "@/lib/fiscal-types";

/* ───────────── Interfaces ───────────── */

interface ProfileRow {
  id: string;
  email: string;
  display_name: string | null;
  is_active: boolean;
  created_at: string;
  roles: string[];
}

interface InviteRow {
  id: string;
  code: string;
  is_active: boolean;
  max_uses: number | null;
  uses_count: number;
  expires_at: string | null;
  note: string | null;
  plan_id: string | null;
  created_at: string;
}

interface PlanConfig {
  id: string;
  name: string;
  description: string;
  price_monthly: number;
  price_annual: number;
  features: string[];
  icon_name: string;
  is_recommended: boolean;
  badge_text: string | null;
  is_active: boolean;
  display_order: number;
  user_limit: number;
  stripe_link_id_monthly?: string;
  stripe_link_url_monthly?: string;
  stripe_link_id_annual?: string;
  stripe_link_url_annual?: string;
}

interface SubscriptionWithProfile {
  id: string;
  user_id: string;
  plan: string;
  status: string;
  current_period_end: string | null;
  created_at: string;
  profiles: {
    email: string;
    display_name: string | null;
  };
}

interface RegraCustomizada {
  id: string;
  nome: string;
  descricao: string;
  campo: string;
  condicao: string;
  severidade: AuditSeverity;
  ativa: boolean;
}

/* ───────────── Constantes ───────────── */

const ALIQUOTAS_ICMS_PADRAO: Record<string, number> = {
  AC: 17, AL: 19, AP: 18, AM: 20, BA: 20, CE: 20, DF: 20, ES: 17,
  GO: 17, MA: 22, MT: 17, MS: 17, MG: 18, PA: 19, PB: 18, PR: 19,
  PE: 20.5, PI: 21, RJ: 22, RN: 18, RS: 17, RO: 17.5, RR: 17,
  SC: 17, SP: 18, SE: 19, TO: 20,
};

const PARAMETROS_BASE = [
  { key: "tolerancia_divergencia", label: "Tolerância de divergência (R$)", value: "0.01", tipo: "number", desc: "Diferença mínima para considerar uma divergência de valor." },
  { key: "limite_zip", label: "Limite máximo do ZIP (MB)", value: "500", tipo: "number", desc: "Tamanho máximo permitido para o arquivo ZIP de upload." },
  { key: "limite_xml_individual", label: "Limite XML individual (MB)", value: "10", tipo: "number", desc: "Tamanho máximo de cada XML dentro do ZIP." },
  { key: "max_xmls_por_lote", label: "Máx. XMLs por lote", value: "5000", tipo: "number", desc: "Quantidade máxima de XMLs processados por lote." },
  { key: "alerta_valor_alto", label: "Alerta valor alto (R$)", value: "10000", tipo: "number", desc: "Notas acima deste valor sem ICMS serão sinalizadas." },
];

const REGRAS_SISTEMA = [
  { code: "R1", layer: 1, title: "Hash SHA-256 do XML original", severity: "INFO" as AuditSeverity, category: "INTEGRIDADE" as AuditCategory, desc: "Cada XML recebe hash SHA-256. Duplicatas byte-a-byte são ignoradas.", status: "ATIVA" as const },
  { code: "R4", layer: 2, title: "vProd = qCom × vUnCom", severity: "MEDIA" as AuditSeverity, category: "MATEMATICA" as AuditCategory, desc: "Valida o produto da quantidade pela unitário com vProd (centavos).", status: "ATIVA" as const },
  { code: "R5", layer: 2, title: "Totais ICMSTot vs soma dos itens", severity: "MEDIA" as AuditSeverity, category: "MATEMATICA" as AuditCategory, desc: "vProd, vICMS, vST, vPIS, vCOFINS, vIPI batem com a soma item-a-item.", status: "ATIVA" as const },
  { code: "R16", layer: 2, title: "Totais IBSCBSTot vs soma dos itens", severity: "ALTA" as AuditSeverity, category: "MATEMATICA" as AuditCategory, desc: "Valida se os totais de IBS e CBS (Reforma) batem com a soma dos itens.", status: "ATIVA" as const },
  { code: "R6", layer: 2, title: "Fórmula oficial do vNF (MOC 7.0 + Reforma)", severity: "ALTA" as AuditSeverity, category: "MATEMATICA" as AuditCategory, desc: "vNF = vProd + vFrete + vST + vIBS + vCBS + vServ − vDesc − vICMSDeson (NT 2023.004).", status: "ATIVA" as const },
  { code: "R7", layer: 3, title: "CST × valores ICMS", severity: "ALTA" as AuditSeverity, category: "TRIBUTARIA" as AuditCategory, desc: "CST 00/10/20/70/90 exigem vBC, pICMS, vICMS > 0. CST 40/41/50/51 exigem vICMS = 0.", status: "ATIVA" as const },
  { code: "R8", layer: 3, title: "CFOP × tipo de operação (tpNF)", severity: "MEDIA" as AuditSeverity, category: "TRIBUTARIA" as AuditCategory, desc: "CFOPs 1/2/3 são entrada (tpNF=0); 5/6/7 são saída (tpNF=1).", status: "ATIVA" as const },
  { code: "R9", layer: 3, title: "Ambiente vs protocolo", severity: "CRITICA" as AuditSeverity, category: "PROTOCOLO" as AuditCategory, desc: "tpAmb=1 exige nProt e dhRecbto. tpAmb=2 = HOMOLOGAÇÃO.", status: "ATIVA" as const },
  { code: "R10", layer: 3, title: "Duplicidade por chave de acesso", severity: "ALTA" as AuditSeverity, category: "INTEGRIDADE" as AuditCategory, desc: "Detecta XMLs distintos com mesma chave de 44 dígitos.", status: "ATIVA" as const },
  { code: "R11", layer: 4, title: "Valor alto com imposto zero", severity: "MEDIA" as AuditSeverity, category: "COERENCIA" as AuditCategory, desc: "vNF > R$ 10.000 com ICMS = 0 e sem CST de isenção é flagado.", status: "ATIVA" as const },
  { code: "R13", layer: 4, title: "Coerência de transporte", severity: "MEDIA" as AuditSeverity, category: "COERENCIA" as AuditCategory, desc: "modFrete 0/1/2 exige transportadora, veículo ou volumes. Ignora transporte próprio (3/4).", status: "ATIVA" as const },
  { code: "R14", layer: 4, title: "Soma de pagamentos vs vNF", severity: "ALTA" as AuditSeverity, category: "COERENCIA" as AuditCategory, desc: "Σ vPag − vTroco = vNF. Considera margem de 10 centavos e ignora notas de ajuste/complementar.", status: "ATIVA" as const },
  { code: "R15", layer: 4, title: "IBS/CBS (Reforma Tributária)", severity: "ALTA" as AuditSeverity, category: "TRIBUTARIA" as AuditCategory, desc: "vIBS = vIBSUF + vIBSMun conforme partilha da Reforma Tributária.", status: "ATIVA" as const },
  { code: "R17", layer: 4, title: "Monitoramento de Transição", severity: "INFO" as AuditSeverity, category: "COERENCIA" as AuditCategory, desc: "Sinaliza incidência simultânea de PIS/COFINS e IBS/CBS (Regra de Transição 2026).", status: "ATIVA" as const },
  { code: "R2", layer: 1, title: "Validação de assinatura digital X509", severity: "CRITICA" as AuditSeverity, category: "ESTRUTURA" as AuditCategory, desc: "Verificação da cadeia ICP-Brasil. Requer Edge Function com OpenSSL.", status: "PENDENTE_BACKEND" as const },
  { code: "R3", layer: 1, title: "Validação de schema XSD oficial", severity: "ALTA" as AuditSeverity, category: "ESTRUTURA" as AuditCategory, desc: "Validação contra procNFe_v4.00.xsd da SEFAZ. Requer parser XSD server-side.", status: "PENDENTE_BACKEND" as const },
  { code: "R12", layer: 4, title: "Padrão anômalo por emitente", severity: "MEDIA" as AuditSeverity, category: "COERENCIA" as AuditCategory, desc: "Compara médias históricas de ICMS/PIS/COFINS por emitente. Requer persistência.", status: "PENDENTE_BACKEND" as const },
];

const SEV_COLORS: Record<AuditSeverity, string> = {
  CRITICA: "#F87171", ALTA: "#FBA94C", MEDIA: "#FCD34D", BAIXA: "#FBBF24", INFO: "#33CCFF",
};

const SOFT_LIGHT_BG = "bg-[#F0F7FF] dark:bg-[#050B26]";
const CARD_STYLE = "bg-white dark:bg-slate-950/40 border border-blue-100 dark:border-white/10 rounded-[40px] shadow-sm";
const NAVY_TEXT = "text-[#0B1F4D] dark:text-white";
const BLUE_ICON_BG = "bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400";

const CAT_ICONS: Record<AuditCategory, any> = {
  INTEGRIDADE: FileCheck,
  MATEMATICA: Calculator,
  TRIBUTARIA: TrendingUp,
  PROTOCOLO: ShieldCheck,
  COERENCIA: AlertTriangle,
  ESTRUTURA: FileCode,
};

/* ───────────── Helpers ───────────── */

function randomCode(len = 8) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let out = "";
  for (let i = 0; i < len; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

/* ───────────── Página Principal ───────────── */

export default function AdminPage() {
  const [loading, setLoading] = useState(true);
  const [silentLoading, setSilentLoading] = useState(false);
  const [errorSync, setErrorSync] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("users");

  // State Global sincronizado
  const [regrasAtivas, setRegrasAtivas] = useState<Record<string, boolean>>({});
  const [params, setParams] = useState<Record<string, string>>({});
   const [aliquotas, setAliquotas] = useState<Record<string, number>>({});
   const [sidebarInfo, setSidebarInfo] = useState({
     version: "v2.4.0 · SPED Fiscal 2026",
     dpo_name: "Encarregado de Dados (DPO)",
     dpo_email: "privacidade@extratorfiscal.app",
     warning: "Atenção: Ferramenta auxiliar. A tomada de decisões requer validação por um Especialista em Regulação."
   });

  async function loadGlobalSettings() {
    setLoading(true);
    try {
      const { data, error } = await supabase.from("app_settings").select("*");
      if (error) throw error;

      const settings: Record<string, any> = {};
      data.forEach(s => settings[s.key] = s.value);

      const safeParse = (str: any, fallback: any) => {
        if (!str) return fallback;
        try { return JSON.parse(str); } catch { return fallback; }
      };

      setRegrasAtivas(
        safeParse(settings.rules_config, Object.fromEntries(REGRAS_SISTEMA.map(r => [r.code, r.status === "ATIVA"])))
      );

      setParams(
        safeParse(settings.fiscal_params, Object.fromEntries(PARAMETROS_BASE.map(p => [p.key, p.value])))
      );

      setAliquotas(
        safeParse(settings.icms_aliquotas, { ...ALIQUOTAS_ICMS_PADRAO })
      );

      setSidebarInfo(
        safeParse(settings.sidebar_info, {
          version: "v2.4.0 · SPED Fiscal 2026",
          dpo_name: "Encarregado de Dados (DPO)",
          dpo_email: "privacidade@extratorfiscal.app",
          warning: "Atenção: Ferramenta auxiliar. A tomada de decisões requer validação por um Especialista em Regulação."
        })
      );
      setErrorSync(null);
    } catch (e: any) {
      // Só mostra tela de erro se for o carregamento inicial ou manual (não silencioso)
      if (!silentLoading) {
        setErrorSync(e.message || "Erro de conexão com o banco de dados");
        toast({ title: "Erro de Sincronização", description: e.message, variant: "destructive" });
      }
    } finally {
      setLoading(false);
      setSilentLoading(false);
    }
  }

  // Sincronização Automática (Background) de 30 em 30 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      if (!loading && !silentLoading) {
        setSilentLoading(true);
        loadGlobalSettings();
      }
    }, 30000); // 30 segundos
    return () => clearInterval(interval);
  }, [loading, silentLoading]);

  // Timeout para evitar loop infinito (Apenas no carregamento inicial/manual)
  useEffect(() => {
    if (!loading || silentLoading) return;
    const timer = setTimeout(() => {
      setLoading(false);
      setErrorSync("O servidor demorou muito para responder. Verifique sua conexão ou privilégios.");
    }, 12000);
    return () => clearTimeout(timer);
  }, [loading, silentLoading]);

  async function saveSetting(key: string, value: any) {
    const { error } = await supabase.from("app_settings").upsert({ 
      key, 
      value: JSON.stringify(value),
      updated_at: new Date().toISOString()
    }, { onConflict: 'key' });
    if (error) {
      toast({ title: "Erro ao salvar", description: error.message, variant: "destructive" });
      return false;
    }
    toast({ title: "Configuração sincronizada", description: "As mudanças já estão ativas para todos os usuários." });
    return true;
  }

  useEffect(() => { 
    document.title = "Administração — DataGrid XML";
    loadGlobalSettings();
  }, []);

  if (loading || errorSync) {
    return (
      <div className={cn("min-h-screen flex flex-col items-center justify-center gap-6 p-6 transition-colors duration-500", SOFT_LIGHT_BG)}>
        <div className="relative">
          <div className="absolute -inset-4 bg-blue-500/10 rounded-full blur-xl animate-pulse" />
          <RefreshCw className={cn("size-12 text-blue-500 relative", loading && "animate-spin")} />
        </div>
        
        <div className="text-center max-w-sm space-y-2">
          <h3 className={cn("text-lg font-black uppercase tracking-widest", NAVY_TEXT)}>
            {loading ? "Sincronizando..." : "Ops! Algo deu errado"}
          </h3>
          <p className="text-slate-400 dark:text-slate-500 text-sm font-medium">
            {loading 
              ? "Estamos preparando as ferramentas administrativas e parâmetros fiscais globais." 
              : errorSync}
          </p>
        </div>

        {!loading && (
          <Button onClick={loadGlobalSettings} className="h-12 px-8 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-bold shadow-xl shadow-blue-500/20 gap-2">
            <RefreshCw className="size-4" /> Tentar Novamente
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="px-6 py-6 lg:px-6 lg:py-6 max-w-7xl mx-auto space-y-4 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-slate-200 dark:border-white/10 pb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="size-4 text-primary animate-pulse" />
            <span className="text-[10px] font-black text-primary uppercase tracking-[0.3em]">Master Console</span>
          </div>
          <h1 className="text-2xl lg:text-4xl font-black text-slate-900 dark:text-white tracking-tight">Administração</h1>
          <p className="text-xs lg:text-sm text-muted-foreground mt-1 font-medium">Gestão Anal�tica de infraestrutura, usuários e inteligência fiscal.</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadGlobalSettings} className="h-10 rounded-xl bg-white/5 border-slate-200 dark:border-white/10 glass hover:bg-primary/10 hover:text-primary transition-all">
          <RefreshCw className={cn("size-4 mr-2", silentLoading && "animate-spin")} /> Sincronizar Agora
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="glass-card p-1 lg:p-1.5 flex overflow-x-auto no-scrollbar border-primary/10">
          <TabsList className="bg-transparent h-auto p-0 flex-1 flex gap-1">
            {[
              { id: "users", label: "Usuários", icon: Users },
              { id: "invites", label: "Convites", icon: KeyRound },
              { id: "finance", label: "Financeiro", icon: CreditCard },
              { id: "params", label: "Parâmetros", icon: Settings },
              { id: "rules", label: "Regras", icon: Zap }
            ].map((t) => (
              <TabsTrigger 
                key={t.id} 
                value={t.id} 
                className="flex-1 min-w-[110px] gap-2 px-4 py-3 text-xs font-bold rounded-xl transition-all data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-lg shadow-primary/20"
              >
                <t.icon className="size-4" /> {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        <div className="min-h-[400px]">
          <TabsContent value="users" className="mt-0 focus-visible:outline-none">
            <UsersTab />
          </TabsContent>

          <TabsContent value="invites" className="mt-0 focus-visible:outline-none">
            <InvitesTab />
          </TabsContent>

          <TabsContent value="finance" className="mt-0 focus-visible:outline-none">
            <FinanceTab />
          </TabsContent>

          <TabsContent value="params" className="mt-0 focus-visible:outline-none">
            <ParamsTab 
              params={params} 
              setParams={setParams} 
              aliquotas={aliquotas} 
              setAliquotas={setAliquotas} 
              sidebarInfo={sidebarInfo}
              setSidebarInfo={setSidebarInfo}
              onSave={saveSetting} 
            />
          </TabsContent>

          <TabsContent value="rules" className="mt-0 focus-visible:outline-none">
            <RulesTab regrasAtivas={regrasAtivas} setRegrasAtivas={setRegrasAtivas} onSave={saveSetting} />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

/* ───────────── ABA: USUÁRIOS ───────────── */

function UsersTab() {
  const { user } = useAuth();
  const [rows, setRows] = useState<ProfileRow[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Estado para criação de usuário
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createdPassword, setCreatedPassword] = useState<string | null>(null);
  const [form, setForm] = useState({
    email: "",
    display_name: "",
    role: "user" as "admin" | "user",
    email_confirm: true,
  });

  async function load() {
    setLoading(true);
    try {
      const [{ data: profiles, error: pe }, { data: roles, error: re }] = await Promise.all([
        supabase.from("profiles").select("id,email,display_name,is_active,created_at").order("created_at", { ascending: false }),
        supabase.from("user_roles").select("user_id,role"),
      ]);
      if (pe || re) throw new Error(pe?.message ?? re?.message);
      
      const byUser = new Map<string, string[]>();
      (roles ?? []).forEach((r: any) => {
        const arr = byUser.get(r.user_id) ?? [];
        arr.push(r.role);
        byUser.set(r.user_id, arr);
      });
      setRows((profiles ?? []).map((p: any) => ({ ...p, roles: byUser.get(p.id) ?? [] })));
    } catch (e: any) {
      toast({ title: "Erro ao carregar", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function toggleActive(row: ProfileRow) {
    const { error } = await supabase.from("profiles").update({ is_active: !row.is_active }).eq("id", row.id);
    if (error) return toast({ title: "Erro", description: error.message, variant: "destructive" });
    toast({ title: row.is_active ? "Usuário desativado" : "Usuário ativado" });
    load();
  }

  async function deleteUser(row: ProfileRow) {
    const { data, error } = await supabase.functions.invoke("admin-delete-user", { body: { user_id: row.id } });
    if (error || (data as any)?.error) return toast({ title: "Erro ao apagar", description: error?.message ?? (data as any)?.error, variant: "destructive" });
    toast({ title: "Usuário apagado" });
    load();
  }

  async function handleCreate() {
    if (!form.email.includes("@")) return toast({ title: "Erro", description: "Email inválido.", variant: "destructive" });
    setCreating(true);
    setCreatedPassword(null);
    try {
      const result = await createUserAdmin({
        email: form.email.trim().toLowerCase(),
        display_name: form.display_name.trim() || undefined,
        role: form.role as any,
        email_confirm: true,
      });

      if (result.ok) {
        toast({ title: "Usuário criado", description: `Acesso liberado para ${form.email}` });
        if (result.password_gerada) setCreatedPassword(result.password_gerada);
        setForm({ email: "", display_name: "", role: "user", email_confirm: true });
        setIsCreateOpen(false);
        load();
      } else {
        toast({ title: "Erro ao criar", description: result.error || "Erro desconhecido na função", variant: "destructive" });
      }
    } catch (e: any) {
      toast({ title: "Erro de Conexão", description: e.message, variant: "destructive" });
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between px-2">
        <div>
          <h2 className="text-xl font-black text-slate-800 dark:text-white">Gestão de Usuários</h2>
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">Controle de acesso e privilégios do sistema</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => { setIsCreateOpen(true); setCreatedPassword(null); }} className="h-10 rounded-xl font-black px-6 bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20 border-0">
            <UserPlus className="size-4 mr-2" /> Novo Usuário
          </Button>
        </div>
      </div>

      <div className="glass-card rounded-[32px] overflow-hidden border-slate-200 dark:border-white/10">
        <div className="overflow-x-auto">
          <Table className="border-collapse">
            <TableHeader className="bg-slate-50/50 dark:bg-slate-900/50 border-0">
              <TableRow className="border-b border-slate-100 dark:border-white/5 hover:bg-transparent">
                <TableHead className="text-slate-500 dark:text-slate-400 pl-8 h-16 font-black text-[10px] uppercase tracking-widest border-0">Usuário / Identidade</TableHead>
                <TableHead className="text-slate-500 dark:text-slate-400 font-black text-[10px] uppercase tracking-widest border-0">Privilégios</TableHead>
                <TableHead className="text-slate-500 dark:text-slate-400 font-black text-[10px] uppercase tracking-widest text-center border-0">Status</TableHead>
                <TableHead className="text-slate-500 dark:text-slate-400 font-black text-[10px] uppercase tracking-widest text-right pr-8 border-0">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="divide-y divide-slate-200 dark:divide-white/5">
              {loading ? (
                <TableRow><TableCell colSpan={4} className="p-12 text-center text-muted-foreground">Carregando usuários...</TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="p-12 text-center text-muted-foreground">Nenhum usuário encontrado</TableCell></TableRow>
              ) : (
                rows.map(r => (
                  <TableRow key={r.id} className="hover:bg-primary/5 transition-all group">
                    <TableCell className="py-5 pl-8">
                      <div className="flex flex-col">
                        <span className="font-bold text-slate-800 dark:text-white text-sm group-hover:text-primary transition-colors">{r.display_name || "—"}</span>
                        <span className="text-[10px] text-slate-400 font-mono mt-0.5">{r.email}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1.5">
                        {r.roles.map(role => (
                          <Badge key={role} variant="outline" className={cn(
                            "text-[9px] font-black uppercase tracking-tighter px-2 py-0.5 rounded-full",
                            role === 'admin' 
                              ? "bg-amber-500/10 text-amber-600 border-amber-500/20" 
                              : "bg-primary/10 text-primary border-primary/20"
                          )}>{role}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={cn(
                        "text-[9px] font-black uppercase rounded-full px-3 py-1",
                        r.is_active 
                          ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20" 
                          : "bg-slate-500/10 text-slate-400 border-slate-500/20"
                      )}>{r.is_active ? "Ativo" : "Inativo"}</Badge>
                    </TableCell>
                    <TableCell className="text-right pr-8 space-x-1">
                      <Button size="sm" variant="ghost" disabled={user?.id === r.id} onClick={() => toggleActive(r)} className="h-9 w-9 p-0 hover:bg-primary/10 text-slate-300 hover:text-primary transition-all rounded-lg">
                        {r.is_active ? <ShieldOff className="size-4" /> : <ShieldCheck className="size-4" />}
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" disabled={user?.id === r.id} className="h-9 w-9 p-0 text-slate-200 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all rounded-lg">
                            <Trash2 className="size-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="rounded-[32px] border-0 shadow-2xl bg-white/95 dark:bg-slate-950/95 backdrop-blur-xl">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="text-xl font-black text-slate-900 dark:text-white">Apagar usuário?</AlertDialogTitle>
                            <AlertDialogDescription className="text-slate-500 font-medium mt-2">Esta ação remove permanentemente <span className="text-primary font-bold">{r.email}</span> do sistema. Não há volta.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter className="mt-8 gap-2">
                            <AlertDialogCancel className="rounded-xl border-slate-200 dark:border-white/10 font-bold h-11">Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteUser(r)} className="rounded-xl bg-red-600 hover:bg-red-500 text-white font-black h-11">EXCLUIR AGORA</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="max-w-sm p-0 border-0 shadow-2xl rounded-[32px] overflow-hidden bg-white/95 dark:bg-slate-950/95 backdrop-blur-xl">
          <DialogHeader className="p-8 pb-4">
            <div className="flex items-center gap-4">
              <div className="size-12 rounded-2xl grid place-items-center bg-primary/10 text-primary">
                <UserPlus className="size-6" />
              </div>
              <div>
                <DialogTitle className="text-xl font-black text-slate-900 dark:text-white leading-tight">
                  Liberar Acesso
                </DialogTitle>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Provisionamento Manual</p>
              </div>
            </div>
          </DialogHeader>
          
          <div className="px-8 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">E-mail Corporativo</Label>
              <Input value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="exemplo@empresa.com" className="h-12 rounded-xl bg-slate-50 dark:bg-white/5 border-slate-200 dark:border-white/10 text-sm focus:ring-primary/20 shadow-inner" />
            </div>
            
            <div className="space-y-1.5">
              <Label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Nome Completo</Label>
              <Input value={form.display_name} onChange={e => setForm({...form, display_name: e.target.value})} placeholder="João Silva" className="h-12 rounded-xl bg-slate-50 dark:bg-white/5 border-slate-200 dark:border-white/10 text-sm focus:ring-primary/20 shadow-inner" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[10px] font-black uppercase text-slate-400 tracking-widest ml-1">Nível de Permissão</Label>
              <Select value={form.role} onValueChange={(v: any) => setForm({...form, role: v})}>
                <SelectTrigger className="h-12 rounded-xl bg-slate-50 dark:bg-white/5 border-slate-200 dark:border-white/10 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl border-slate-200 dark:border-white/10">
                  <SelectItem value="user" className="font-bold">Analista Fiscal (Padrão)</SelectItem>
                  <SelectItem value="admin" className="font-bold text-primary">Administrador Master</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {createdPassword && (
              <div className="p-5 rounded-2xl bg-primary/5 border border-primary/20 space-y-3 animate-in zoom-in-95">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[9px] font-black uppercase text-primary tracking-widest mb-1">Senha Gerada</div>
                    <code className="text-2xl font-mono font-black text-slate-900 dark:text-white tracking-widest">{createdPassword}</code>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => { navigator.clipboard.writeText(createdPassword); toast({ title: "Copiado!" }); }} className="text-primary h-11 w-11 rounded-xl bg-primary/10 hover:bg-primary/20">
                    <Copy className="size-5" />
                  </Button>
                </div>
                <p className="text-[9px] text-primary/70 font-bold uppercase text-center">Copie agora. Ela não será exibida novamente.</p>
              </div>
            )}
          </div>

          <DialogFooter className="p-8">
            <Button onClick={handleCreate} disabled={creating || !form.email} className="w-full h-14 rounded-xl font-black text-white text-sm bg-primary hover:bg-primary/90 shadow-xl shadow-primary/20 border-0 group">
              {creating ? <Loader2 className="size-5 animate-spin mr-2" /> : <ShieldCheck className="size-5 mr-2 group-hover:scale-110 transition-transform" />}
              {creating ? "PROVISIONANDO..." : "LIBERAR ACESSO IMEDIATO"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ───────────── ABA: CONVITES ───────────── */

function InvitesTab() {
  const [rows, setRows] = useState<InviteRow[]>([]);
  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<InviteRow | null>(null);
  const [open, setOpen] = useState(false);
  const [inviteUsersMap, setInviteUsersMap] = useState<Record<string, any[]>>({});

  async function load() {
    setLoading(true);
    try {
      const [{ data: invites }, { data: plansRes }, { data: profiles }] = await Promise.all([
        supabase.from("invites").select("*").order("created_at", { ascending: false }),
        supabase.from("plans_config").select("id, name, user_limit").order("display_order", { ascending: true }),
        supabase.from("profiles").select("display_name, email, raw_user_meta_data")
      ]);
      const map: Record<string, any[]> = {};
      profiles?.forEach(p => {
        const code = p.raw_user_meta_data?.invite_code || (p as any).invite_code;
        if (code) { if (!map[code]) map[code] = []; map[code].push(p); }
      });
      setInviteUsersMap(map);
      setRows((invites ?? []) as InviteRow[]);
      setPlans(plansRes ?? []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function save() {
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {
        code: editing.code.trim().toUpperCase(),
        is_active: editing.is_active,
        max_uses: editing.max_uses && editing.max_uses > 0 ? Number(editing.max_uses) : null,
        expires_at: editing.expires_at || null,
        plan_id: editing.plan_id && editing.plan_id !== "" ? editing.plan_id : null,
        note: editing.note ?? null,
      };

      const q = (editing.id && editing.id !== "") 
        ? supabase.from("invites").update(payload).eq("id", editing.id) 
        : supabase.from("invites").insert({ ...payload, created_by: (await supabase.auth.getUser()).data.user?.id });

      const { error } = await q;
      if (error) throw error;

      toast({ title: "Convite salvo", description: `Código ${payload.code} processado com sucesso.` });
      setOpen(false);
      load();
    } catch (e: any) {
      toast({ title: "Erro ao salvar", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  async function deleteInvite(id: string) {
    const { error } = await supabase.from("invites").delete().eq("id", id);
    if (error) return toast({ title: "Erro", description: error.message, variant: "destructive" });
    toast({ title: "Convite removido" });
    load();
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold dark:text-white flex items-center gap-2">
            <KeyRound className="size-5 text-primary" /> Gestão de Convites
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={load} className="h-10 rounded-xl bg-white/5 border-slate-200 dark:border-white/10 glass">
            <RefreshCw className={cn("size-4 mr-2", loading && "animate-spin")} /> Sincronizar
          </Button>
          <Button size="sm" onClick={() => { setEditing({ id: "", code: randomCode(), is_active: true, max_uses: null, uses_count: 0, expires_at: null, note: "", plan_id: null, created_at: "" }); setOpen(true); }} className="h-10 rounded-xl font-bold px-6 bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/20 border-0">
            <Plus className="size-4 mr-2" /> Novo Convite
          </Button>
        </div>
      </div>

      <div className="glass-card rounded-[32px] overflow-hidden border-slate-200 dark:border-white/10">
        <div className="overflow-x-auto">
          <Table className="border-collapse">
            <TableHeader className="bg-slate-50/50 dark:bg-slate-900/50 border-0">
              <TableRow className="border-b border-slate-100 dark:border-white/5 hover:bg-transparent">
                <TableHead className="text-slate-500 dark:text-slate-400 pl-8 h-16 font-black text-[10px] uppercase tracking-widest border-0">Código / Plano</TableHead>
                <TableHead className="text-slate-500 dark:text-slate-400 font-black text-[10px] uppercase tracking-widest border-0 text-center">Usos / Limite</TableHead>
                <TableHead className="text-slate-500 dark:text-slate-400 font-black text-[10px] uppercase tracking-widest border-0 text-center">Status</TableHead>
                <TableHead className="text-slate-500 dark:text-slate-400 font-black text-[10px] uppercase tracking-widest border-0">Nota</TableHead>
                <TableHead className="text-slate-500 dark:text-slate-400 font-black text-[10px] uppercase tracking-widest border-0 text-right pr-8">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody className="divide-y divide-slate-200 dark:divide-white/5">
              {loading ? (
                <TableRow><TableCell colSpan={5} className="p-12 text-center text-muted-foreground">Carregando...</TableCell></TableRow>
              ) : (
                rows.map((r) => {
                  const plan = plans.find(p => p.id === r.plan_id);
                  const users = inviteUsersMap[r.code] || [];
                  const limit = r.max_uses || plan?.user_limit || 0;
                  return (
                    <TableRow key={r.id} className="hover:bg-primary/5 transition-colors group">
                      <TableCell className="p-4 pl-8">
                        <div className="flex flex-col">
                          <span className="font-mono font-bold text-slate-800 dark:text-white text-sm tracking-wider">{r.code}</span>
                          <span className="text-[10px] text-primary/70 font-bold uppercase">{plan?.name || "Padrão"}</span>
                        </div>
                      </TableCell>
                      <TableCell className="p-4 text-center">
                        <div className="text-sm font-bold text-slate-800 dark:text-white">
                          {users.length} <span className="text-slate-300 dark:text-white/20">/ {limit || '∞'}</span>
                        </div>
                      </TableCell>
                      <TableCell className="p-4 text-center">
                        <Badge className={cn(
                          "px-2 py-0.5 rounded-full text-[9px] font-black uppercase",
                          r.is_active ? "bg-emerald-500/15 text-emerald-500 border-emerald-500/25" : "bg-slate-500/10 text-slate-400 border-slate-500/20"
                        )}>
                          {r.is_active ? "Ativo" : "Pausado"}
                        </Badge>
                      </TableCell>
                      <TableCell className="p-4 text-[11px] text-slate-400 italic truncate max-w-[200px]">
                        {r.note || "—"}
                      </TableCell>
                      <TableCell className="p-4 text-right pr-8">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => { setEditing(r); setOpen(true); }} className="h-8 w-8 p-0 text-slate-400 hover:text-primary hover:bg-primary/10 rounded-lg">
                            <Edit2 className="size-4" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-slate-300 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg">
                                <Trash2 className="size-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="rounded-3xl border-0 shadow-2xl bg-white dark:bg-slate-950">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="text-xl font-bold text-slate-900 dark:text-white">Apagar Convite?</AlertDialogTitle>
                                <AlertDialogDescription className="text-slate-500">O código <span className="text-primary font-bold">{r.code}</span> deixará de funcionar imediatamente.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter className="mt-6">
                                <AlertDialogCancel className="rounded-xl border-slate-200 dark:border-white/10">Cancelar</AlertDialogCancel>
                                <AlertDialogAction onClick={() => deleteInvite(r.id)} className="rounded-xl bg-red-600 hover:bg-red-500 text-white font-bold">Excluir Código</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={cn("max-w-sm p-0 border-0 shadow-2xl rounded-[32px] overflow-hidden bg-[#F0F7FF] dark:bg-slate-950")}>
          <DialogHeader className="p-6 pb-2">
            <div className="flex items-center gap-3">
              <div className="size-11 rounded-full grid place-items-center bg-white shadow-sm border border-blue-100">
                <KeyRound className="size-5 text-sky-500" />
              </div>
              <div>
                <p className="text-[8px] font-black uppercase tracking-[0.2em] text-sky-500/60 mb-0">Convites</p>
                <DialogTitle className="text-lg font-black text-[#0B1F4D] dark:text-white leading-tight">
                  {editing?.id && editing.id !== "" ? "Editar Convite" : "Novo Convite"}
                </DialogTitle>
              </div>
            </div>
          </DialogHeader>
          
          {editing && (
            <div className="px-6 py-1 space-y-4">
              <div className="space-y-1">
                <Label className="text-[9px] font-bold uppercase text-slate-400 tracking-widest ml-1">Código de Acesso</Label>
                <div className="flex gap-2">
                  <Input value={editing.code} onChange={e => setEditing({...editing, code: e.target.value.toUpperCase()})} className="h-11 rounded-xl bg-white border-blue-100 font-mono font-black text-base text-sky-600 tracking-widest shadow-sm px-4" />
                  <Button variant="outline" size="icon" onClick={() => setEditing({...editing, code: randomCode()})} className="h-11 w-11 rounded-xl bg-white border-blue-100 shadow-sm hover:bg-blue-50"><RefreshCw className="size-4 text-blue-500" /></Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[9px] font-bold uppercase text-slate-400 tracking-widest ml-1">Limite Usos</Label>
                  <Input type="number" placeholder="Ilimitado" value={editing.max_uses || ""} onChange={e => setEditing({...editing, max_uses: e.target.value ? Number(e.target.value) : null})} className="h-11 rounded-xl bg-white border-blue-100 shadow-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[9px] font-bold uppercase text-slate-400 tracking-widest ml-1">Expiração</Label>
                  <Input type="date" value={editing.expires_at ? editing.expires_at.split('T')[0] : ""} onChange={e => setEditing({...editing, expires_at: e.target.value ? new Date(e.target.value).toISOString() : null})} className="h-11 rounded-xl bg-white border-blue-100 shadow-sm" />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-[9px] font-bold uppercase text-slate-400 tracking-widest ml-1">Plano Destino</Label>
                <Select value={editing.plan_id || "null"} onValueChange={v => setEditing({...editing, plan_id: v === "null" ? null : v})}>
                  <SelectTrigger className="h-11 rounded-xl bg-white border-blue-100 shadow-sm">
                    <SelectValue placeholder="Plano Padrão" />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl border-blue-100">
                    <SelectItem value="null">Plano Padrão (Básico)</SelectItem>
                    {plans.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label className="text-[9px] font-bold uppercase text-slate-400 tracking-widest ml-1">Nota Admin</Label>
                <Input value={editing.note || ""} onChange={e => setEditing({...editing, note: e.target.value})} placeholder="Ex: Parceria..." className="h-11 rounded-xl bg-white border-blue-100 shadow-sm" />
              </div>

              <div className="flex items-center justify-between p-4 bg-white border border-blue-100 rounded-2xl shadow-sm">
                <div className="flex flex-col">
                  <span className="text-[11px] font-bold text-[#0B1F4D] dark:text-white">Status</span>
                  <span className="text-[8px] text-slate-400 font-medium uppercase tracking-wider">{editing.is_active ? "Ativo" : "Pausado"}</span>
                </div>
                <Switch checked={editing.is_active} onCheckedChange={v => setEditing({...editing, is_active: v})} />
              </div>
            </div>
          )}
          <DialogFooter className="p-6 pt-3">
            <Button onClick={save} disabled={saving} className="w-full h-12 rounded-full font-black text-white text-sm bg-sky-500 hover:bg-sky-400 shadow-lg shadow-sky-500/20 border-0">
              {saving ? <Loader2 className="size-4 animate-spin mr-2" /> : <Save className="size-4 mr-2" />}
              {saving ? "SALVANDO..." : "SALVAR ALTERAÇÕES"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ───────────── ABA: FINANCEIRO ───────────── */

function FinanceTab() {
  const [subs, setSubs] = useState<SubscriptionWithProfile[]>([]);
  const [plans, setPlans] = useState<PlanConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [financeSubTab, setFinanceSubTab] = useState("subscribers");
  
  // Estado para edição de plano
  const [isPlanDialogOpen, setIsPlanDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<any>(null);
  const [annualDiscount, setAnnualDiscount] = useState(20);

  // Estado para edição de assinante
  const [isSubDialogOpen, setIsSubDialogOpen] = useState(false);
  const [editingSub, setEditingSub] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");

  async function loadData() {
    setLoading(true);
    try {
      const [subsRes, plansRes] = await Promise.all([
        supabase.from("subscriptions").select("*, profiles:user_id(email, display_name)").order("created_at", { ascending: false }),
        supabase.from("plans_config").select("*").order("display_order", { ascending: true }),
      ]);
      setSubs(subsRes.data as any || []);
      setPlans(plansRes.data as any || []);
    } finally { setLoading(false); }
  }

  useEffect(() => { loadData(); }, []);

  async function handleSavePlan() {
    if (!editingPlan) return;
    const { error } = await supabase.from("plans_config").upsert(editingPlan);
    if (error) return toast({ title: "Erro", description: error.message, variant: "destructive" });
    toast({ title: editingPlan.id ? "Plano atualizado" : "Plano criado" });
    setIsPlanDialogOpen(false);
    loadData();
  }

  async function handleDeletePlan(id: string) {
    if (!window.confirm("Tem certeza que deseja excluir este plano?")) return;
    const { error } = await supabase.from("plans_config").delete().eq("id", id);
    if (error) return toast({ title: "Erro", description: error.message, variant: "destructive" });
    toast({ title: "Plano excluído" });
    loadData();
  }

  async function handleUpdateSubscription() {
    if (!editingSub) return;
    const { error } = await supabase.from("subscriptions").update({
      plan: editingSub.plan,
      status: editingSub.status,
      current_period_end: editingSub.current_period_end
    }).eq("id", editingSub.id);

    if (error) return toast({ title: "Erro ao atualizar", description: error.message, variant: "destructive" });
    toast({ title: "Assinatura atualizada" });
    setIsSubDialogOpen(false);
    loadData();
  }

  const filteredSubs = subs.filter(s => 
    s.profiles?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (s.profiles?.display_name?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const stats = {
    mrr: subs.filter(s => s.status === 'active').reduce((acc, curr) => {
      const plan = plans.find(p => p.name.toLowerCase().includes(curr.plan.toLowerCase()));
      return acc + (plan?.price_monthly || 0);
    }, 0),
    activeCount: subs.filter(s => s.status === 'active').length,
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className={cn("p-6 flex items-center gap-4 transition-all hover:scale-[1.02] duration-300", CARD_STYLE)}>
          <div className={cn("size-12 rounded-2xl flex items-center justify-center", BLUE_ICON_BG)}><TrendingUp className="size-6" /></div>
          <div><p className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-400">MRR Ativo</p><p className={cn("text-2xl font-black", NAVY_TEXT)}>{fmtBRL(stats.mrr)}</p></div>
        </Card>
        <Card className={cn("p-6 flex items-center gap-4 transition-all hover:scale-[1.02] duration-300", CARD_STYLE)}>
          <div className={cn("size-12 rounded-2xl flex items-center justify-center", BLUE_ICON_BG)}><Users className="size-6" /></div>
          <div><p className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-400">Assinantes</p><p className={cn("text-2xl font-black", NAVY_TEXT)}>{stats.activeCount}</p></div>
        </Card>
        <Card className={cn("p-6 flex items-center gap-4 transition-all hover:scale-[1.02] duration-300", CARD_STYLE)}>
          <div className={cn("size-12 rounded-2xl flex items-center justify-center", BLUE_ICON_BG)}><Sparkles className="size-6" /></div>
          <div><p className="text-[10px] font-black uppercase tracking-[0.15em] text-slate-400">Planos Ativos</p><p className={cn("text-2xl font-black", NAVY_TEXT)}>{plans.filter(p => p.is_active).length}</p></div>
        </Card>
      </div>

      <Tabs value={financeSubTab} onValueChange={setFinanceSubTab} className="space-y-6">
        <TabsList className="bg-white/5 border border-white/10 p-1">
          <TabsTrigger value="subscribers" className="gap-2"><Users className="size-4" /> Assinantes</TabsTrigger>
          <TabsTrigger value="manage_plans" className="gap-2"><Sparkles className="size-4" /> Gestão de Planos</TabsTrigger>
        </TabsList>

        <TabsContent value="subscribers" className="space-y-6">
          <Card className={cn("overflow-hidden", CARD_STYLE)}>
            <div className="p-4 border-b border-blue-100 dark:border-white/10 flex gap-4 items-center bg-blue-50/20">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-slate-400" />
                <Input 
                  placeholder="Buscar usuário..." 
                  className="pl-10 h-10 rounded-xl bg-white border-blue-100 text-[#0B1F4D] dark:bg-white/5" 
                  value={searchTerm} 
                  onChange={e => setSearchTerm(e.target.value)} 
                />
              </div>
              <Button variant="outline" size="sm" onClick={loadData} className="h-10 px-4 border-blue-200 dark:border-white/10 rounded-full bg-white dark:bg-white/5 text-blue-600">
                <RefreshCw className={cn("size-4 mr-2", loading && "animate-spin")} /> Sincronizar
              </Button>
            </div>
            <Table>
              <TableHeader className="bg-blue-50/50 dark:bg-white/5 uppercase font-bold text-[10px] tracking-widest">
                <TableRow className="border-blue-100 dark:border-white/10 hover:bg-transparent">
                  <TableHead className="text-slate-500 dark:text-slate-400 pl-8 h-14">Cliente</TableHead>
                  <TableHead className="text-slate-500 dark:text-slate-400 text-center">Plano</TableHead>
                  <TableHead className="text-slate-500 dark:text-slate-400 text-center">Status</TableHead>
                  <TableHead className="text-slate-500 dark:text-slate-400">Vencimento</TableHead>
                  <TableHead className="text-slate-500 dark:text-slate-400 text-right pr-8">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? <TableRow><TableCell colSpan={5} className="text-center py-10 opacity-50">Carregando...</TableCell></TableRow> :
                 filteredSubs.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-10 opacity-50">Nenhuma assinatura encontrada</TableCell></TableRow> :
                 filteredSubs.map(s => (
                  <TableRow key={s.id} className="border-blue-50 dark:border-white/5 hover:bg-blue-50/30 dark:hover:bg-white/5 transition-all group">
                    <TableCell className="pl-8 py-5">
                      <div className="flex flex-col">
                        <span className="font-bold text-[#0B1F4D] dark:text-white group-hover:text-blue-600 transition-colors">{s.profiles?.display_name || "—"}</span>
                        <span className="text-[10px] text-slate-400 font-medium">{s.profiles?.email}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="uppercase text-[9px] font-black tracking-tight border-blue-100 dark:border-white/10 text-slate-500 dark:text-white/40">{s.plan}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={cn(
                        "text-[9px] font-black uppercase",
                        s.status === 'active' ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20" : "bg-slate-100 dark:bg-white/10 text-slate-400 dark:text-white/40"
                      )}>{s.status}</Badge>
                    </TableCell>
                    <TableCell className="text-[10px] font-medium text-slate-400">{s.current_period_end ? fmtDate(s.current_period_end) : '—'}</TableCell>
                    <TableCell className="text-right pr-8">
                      <Button variant="ghost" size="sm" onClick={() => { setEditingSub({...s}); setIsSubDialogOpen(true); }} className="hover:bg-blue-50 dark:hover:bg-white/5 text-slate-400 hover:text-blue-600 rounded-full">
                        <Edit2 className="size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                 ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="manage_plans" className="space-y-6">
          <div className="flex justify-end">
            <Button onClick={() => { setEditingPlan({ name: "", price_monthly: 0, price_annual: 0, features: [], is_active: true, display_order: plans.length + 1 }); setIsPlanDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-500">
              <Plus className="size-4 mr-2" /> Criar Novo Plano
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plans.map(plan => (
              <Card key={plan.id} className={cn(
                "relative flex flex-col justify-between transition-all duration-500 border rounded-3xl p-7 shadow-sm",
                plan.is_active 
                  ? "bg-white border-slate-200 hover:border-primary/50 shadow-slate-200/50" 
                  : "bg-slate-50 border-slate-100 opacity-50 grayscale"
              )}>
                <div className="space-y-5">
                  <div className="flex justify-between items-start">
                    <div className="max-w-[70%]">
                      <h3 className="text-xl font-black text-slate-900 tracking-tight">{plan.name}</h3>
                      <p className="text-[10px] font-black uppercase tracking-widest text-primary mt-1">{plan.description || "Sem descrição"}</p>
                    </div>
                    {plan.is_recommended && (
                      <Badge className="bg-primary text-white border-none text-[8px] font-black px-2 py-0.5 shadow-md shadow-primary/20">
                        RECOMENDADO
                      </Badge>
                    )}
                  </div>

                  <div className="flex flex-col gap-1 py-3 border-y border-slate-100">
                    <div className="flex items-baseline gap-1 text-slate-900">
                      <span className="text-sm font-bold text-slate-400">R$</span>
                      <span className="text-3xl font-black">{fmtNum(plan.price_monthly)}</span>
                      <span className="text-xs font-bold text-slate-400 uppercase">/mês</span>
                    </div>
                    <div className="text-[10px] font-bold text-emerald-600 flex items-center gap-1.5 uppercase tracking-wide">
                      <Percent className="size-3" /> Anual: {fmtBRL(plan.price_annual)}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-primary/5 border border-primary/10 w-fit">
                      <Users className="size-3.5 text-primary" />
                      <span className="text-[10px] font-black text-primary uppercase tracking-widest">
                        Limite: {plan.user_limit} usuário(s)
                      </span>
                    </div>
                    
                    <ul className="space-y-2">
                      {plan.features?.slice(0, 4).map((f, i) => (
                        <li key={i} className="flex gap-2.5 items-center">
                          <div className="size-4 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0">
                            <CheckCircle2 className="size-2.5 text-emerald-500" />
                          </div>
                          <span className="text-[11px] text-slate-600 font-medium truncate">{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="flex gap-3 mt-8">
                  <Button 
                    variant="outline" 
                    className="flex-1 h-11 bg-white border-slate-200 hover:border-primary/30 hover:bg-slate-50 text-slate-700 text-[10px] font-black uppercase tracking-[0.2em] rounded-xl transition-all shadow-sm" 
                    onClick={() => { 
                      setEditingPlan({...plan}); 
                      const discount = plan.price_monthly > 0 ? Math.round((1 - (plan.price_annual / (plan.price_monthly * 12))) * 100) : 0;
                      setAnnualDiscount(discount);
                      setIsPlanDialogOpen(true); 
                    }}
                  >
                    Editar Plano
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="size-11 rounded-xl text-red-400 hover:text-red-600 hover:bg-red-50" 
                    onClick={() => handleDeletePlan(plan.id)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* MODAL EDIÇÃO PLANO */}
      <Dialog open={isPlanDialogOpen} onOpenChange={setIsPlanDialogOpen}>
        <DialogContent className={cn("max-w-sm p-0 border-0 shadow-2xl rounded-[32px] overflow-hidden bg-[#F0F7FF] dark:bg-slate-950")}>
          <DialogHeader className="p-6 pb-2">
            <div className="flex items-center gap-3">
              <div className="size-11 rounded-full grid place-items-center bg-white shadow-sm border border-blue-100">
                <Sparkles className="size-5 text-blue-500" />
              </div>
              <div>
                <p className="text-[8px] font-black uppercase tracking-[0.2em] text-blue-500/60 mb-0">Financeiro</p>
                <DialogTitle className="text-lg font-black text-[#0B1F4D] dark:text-white leading-tight">
                  {editingPlan?.id ? "Editar Plano" : "Novo Plano"}
                </DialogTitle>
              </div>
            </div>
          </DialogHeader>
          
          {editingPlan && (
            <div className="px-6 py-1 space-y-4 max-h-[60vh] overflow-y-auto custom-scrollbar">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[9px] font-bold uppercase text-slate-400 tracking-widest ml-1">Nome</Label>
                  <Input value={editingPlan.name} onChange={e => setEditingPlan({...editingPlan, name: e.target.value})} className="h-11 rounded-xl bg-white border-blue-100 text-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[9px] font-bold uppercase text-slate-400 tracking-widest ml-1">Ícone</Label>
                  <Input value={editingPlan.icon_name} onChange={e => setEditingPlan({...editingPlan, icon_name: e.target.value})} className="h-11 rounded-xl bg-white border-blue-100 text-sm" />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-[9px] font-bold uppercase text-slate-400 tracking-widest ml-1">Descrição</Label>
                <Input value={editingPlan.description || ""} onChange={e => setEditingPlan({...editingPlan, description: e.target.value})} className="h-11 rounded-xl bg-white border-blue-100 text-sm" />
              </div>

              <div className="grid grid-cols-3 gap-3 p-3 bg-white/50 rounded-2xl border border-blue-50">
                <div className="space-y-1 text-center">
                  <Label className="text-[8px] font-black text-blue-400 uppercase tracking-tight">Mensal</Label>
                  <Input type="number" step="0.01" value={editingPlan.price_monthly} onChange={e => {
                    const monthly = Number(e.target.value);
                    const annual = (monthly * 12) * (1 - annualDiscount / 100);
                    setEditingPlan({...editingPlan, price_monthly: monthly, price_annual: Number(annual.toFixed(2))});
                  }} className="h-10 rounded-lg bg-white border-blue-100 text-xs font-bold text-center" />
                </div>
                <div className="space-y-1 text-center">
                  <Label className="text-[8px] font-black text-emerald-400 uppercase tracking-tight">Desc. %</Label>
                  <Input type="number" value={annualDiscount} onChange={e => {
                    const disc = Number(e.target.value);
                    setAnnualDiscount(disc);
                    const annual = (Number(editingPlan.price_monthly || 0) * 12) * (1 - disc / 100);
                    setEditingPlan({...editingPlan, price_annual: Number(annual.toFixed(2))});
                  }} className="h-10 rounded-lg bg-white border-blue-100 text-xs font-bold text-center" />
                </div>
                <div className="space-y-1 text-center">
                  <Label className="text-[8px] font-black text-blue-400 uppercase tracking-tight">Anual</Label>
                  <Input type="number" step="0.01" value={editingPlan.price_annual} onChange={e => setEditingPlan({...editingPlan, price_annual: Number(e.target.value)})} className="h-10 rounded-lg bg-white border-blue-100 text-xs font-bold text-center" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[9px] font-bold uppercase text-slate-400 tracking-widest ml-1">Usuários</Label>
                  <Input type="number" value={editingPlan.user_limit} onChange={e => setEditingPlan({...editingPlan, user_limit: Number(e.target.value)})} className="h-11 rounded-xl bg-white border-blue-100 text-sm text-center" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[9px] font-bold uppercase text-slate-400 tracking-widest ml-1">Ordem</Label>
                  <Input type="number" value={editingPlan.display_order} onChange={e => setEditingPlan({...editingPlan, display_order: Number(e.target.value)})} className="h-11 rounded-xl bg-white border-blue-100 text-sm text-center" />
                </div>
              </div>

              <div className="space-y-1">
                <Label className="text-[9px] font-bold uppercase text-slate-400 tracking-widest ml-1">Recursos (Um por linha)</Label>
                <Textarea value={editingPlan.features?.join('\n')} onChange={e => setEditingPlan({...editingPlan, features: e.target.value.split('\n')})} className="h-24 rounded-xl bg-white border-blue-100 text-[11px] leading-relaxed" />
              </div>

              <div className="flex items-center gap-3 bg-white border border-blue-100 p-4 rounded-2xl shadow-sm">
                <Switch checked={editingPlan.is_active} onCheckedChange={v => setEditingPlan({...editingPlan, is_active: v})} />
                <div className="flex flex-col">
                  <span className="text-[11px] font-bold text-[#0B1F4D] dark:text-white uppercase">Vendas Ativas</span>
                  <span className="text-[8px] text-slate-400 font-medium uppercase">Aparece no site</span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="p-6 pt-3">
            <Button onClick={handleSavePlan} className="w-full h-12 rounded-full font-black text-white text-sm bg-blue-600 hover:bg-blue-500 shadow-lg shadow-blue-500/20 border-0">
              SALVAR ALTERAÇÕES
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL EDIÇÃO ASSINANTE */}
      <Dialog open={isSubDialogOpen} onOpenChange={setIsSubDialogOpen}>
        <DialogContent className={cn("max-w-sm p-0 border-0 shadow-2xl rounded-[32px] overflow-hidden bg-[#F0F7FF] dark:bg-slate-950")}>
          <DialogHeader className="p-6 pb-2">
            <div className="flex items-center gap-3">
              <div className="size-11 rounded-full grid place-items-center bg-white shadow-sm border border-blue-100">
                <UserCog className="size-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-[8px] font-black uppercase tracking-[0.2em] text-emerald-500/60 mb-0">Gestão</p>
                <DialogTitle className="text-lg font-black text-[#0B1F4D] dark:text-white leading-tight">
                  Assinatura
                </DialogTitle>
              </div>
            </div>
          </DialogHeader>
          
          {editingSub && (
            <div className="px-6 py-1 space-y-4">
              <div className="space-y-1">
                <Label className="text-[9px] font-bold uppercase text-slate-400 tracking-widest ml-1">Plano Atual</Label>
                <Select value={editingSub.plan} onValueChange={v => setEditingSub({...editingSub, plan: v})}>
                  <SelectTrigger className="h-11 rounded-xl bg-white border-blue-100 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl border-blue-100">
                    {plans.map(p => <SelectItem key={p.id} value={p.name.toLowerCase().replace("plano ", "")}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[9px] font-bold uppercase text-slate-400 tracking-widest ml-1">Status do Ciclo</Label>
                <Select value={editingSub.status} onValueChange={v => setEditingSub({...editingSub, status: v})}>
                  <SelectTrigger className="h-11 rounded-xl bg-white border-blue-100 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl border-blue-100">
                    <SelectItem value="active">Ativa</SelectItem>
                    <SelectItem value="inactive">Inativa</SelectItem>
                    <SelectItem value="canceled">Cancelada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[9px] font-bold uppercase text-slate-400 tracking-widest ml-1">Vencimento Próximo</Label>
                <Input type="date" value={editingSub.current_period_end?.split('T')[0] || ''} onChange={e => setEditingSub({...editingSub, current_period_end: e.target.value ? new Date(e.target.value).toISOString() : null})} className="h-11 rounded-xl bg-white border-blue-100 text-sm" />
              </div>
            </div>
          )}
          <DialogFooter className="p-6 pt-3">
            <Button onClick={handleUpdateSubscription} className="w-full h-12 rounded-full font-black text-white text-sm bg-emerald-600 hover:bg-emerald-500 shadow-lg shadow-emerald-500/20 border-0">
              CONFIRMAR ALTERAÇÃO
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ───────────── ABA: PARÂMETROS ───────────── */

function ParamsTab({ params, setParams, aliquotas, setAliquotas, sidebarInfo, setSidebarInfo, onSave }: any) {
  const handleSave = async () => {
    const ok1 = await onSave('fiscal_params', params);
    const ok2 = await onSave('icms_aliquotas', aliquotas);
    const ok3 = await onSave('sidebar_info', sidebarInfo);
    if (ok1 && ok2 && ok3) {
      toast({ title: "Configurações globais salvas" });
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <Card className={cn("p-6 space-y-6", CARD_STYLE)}>
          <div className="flex items-center gap-3 border-b border-blue-100 dark:border-white/5 pb-4">
            <div className={cn("size-10 rounded-xl grid place-items-center", BLUE_ICON_BG)}><Activity className="size-5" /></div>
            <div><h3 className={cn("font-black text-sm", NAVY_TEXT)}>Operação</h3><p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">Limites e validações do motor.</p></div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {PARAMETROS_BASE.map(p => (
              <div key={p.key} className="space-y-2">
                <Label className="text-[10px] font-black uppercase text-slate-400 tracking-[0.15em] ml-1">{p.label}</Label>
                <Input value={params[p.key]} onChange={e => setParams({...params, [p.key]: e.target.value})} className="h-11 rounded-xl bg-white border-blue-100 text-sm font-bold text-[#0B1F4D] dark:bg-white/5 shadow-sm" />
                <p className="text-[9px] text-slate-400 italic px-1">{p.desc}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* SEÇÃO: SIDEBAR */}
        <Card className={cn("p-6 space-y-6", CARD_STYLE)}>
          <div className="flex items-center gap-3 border-b border-blue-100 dark:border-white/5 pb-4">
            <div className={cn("size-10 rounded-xl grid place-items-center", BLUE_ICON_BG)}><Globe className="size-5" /></div>
            <div><h3 className={cn("font-black text-sm", NAVY_TEXT)}>Rodapé Lateral</h3><p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">Institucional e DPO.</p></div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase text-slate-400 tracking-[0.15em] ml-1">Versão / Título</Label>
              <Input value={sidebarInfo.version} onChange={e => setSidebarInfo({...sidebarInfo, version: e.target.value})} className="h-11 rounded-xl bg-white border-blue-100 text-sm font-bold text-[#0B1F4D] dark:bg-white/5 shadow-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase text-slate-400 tracking-[0.15em] ml-1">Encarregado (DPO)</Label>
              <Input value={sidebarInfo.dpo_name} onChange={e => setSidebarInfo({...sidebarInfo, dpo_name: e.target.value})} className="h-11 rounded-xl bg-white border-blue-100 text-sm font-bold text-[#0B1F4D] dark:bg-white/5 shadow-sm" />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase text-slate-400 tracking-[0.15em] ml-1">E-mail DPO</Label>
              <Input value={sidebarInfo.dpo_email} onChange={e => setSidebarInfo({...sidebarInfo, dpo_email: e.target.value})} className="h-11 rounded-xl bg-white border-blue-100 text-sm font-bold text-[#0B1F4D] dark:bg-white/5 shadow-sm" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-[10px] font-black uppercase text-slate-400 tracking-[0.15em] ml-1">Aviso Legal</Label>
              <Textarea value={sidebarInfo.warning} onChange={e => setSidebarInfo({...sidebarInfo, warning: e.target.value})} className="h-24 rounded-xl bg-white border-blue-100 text-xs font-medium text-[#0B1F4D] dark:bg-white/5 shadow-sm" />
            </div>
          </div>
          
          <Button onClick={handleSave} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-black uppercase tracking-widest text-xs h-14 rounded-full shadow-xl shadow-blue-500/20">
            <Save className="size-4 mr-2" /> Salvar Configurações Globais
          </Button>
        </Card>
      </div>

      <Card className={cn("p-6 space-y-6", CARD_STYLE)}>
        <div className="flex items-center gap-3 border-b border-blue-100 dark:border-white/5 pb-4">
          <div className={cn("size-10 rounded-xl grid place-items-center", BLUE_ICON_BG)}><Percent className="size-5" /></div>
          <div><h3 className={cn("font-black text-sm", NAVY_TEXT)}>ICMS por UF</h3><p className="text-[10px] text-slate-400 font-medium uppercase tracking-wider">Alíquotas modais.</p></div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {Object.entries(aliquotas).sort(([a], [b]) => a.localeCompare(b)).map(([uf, aliq]) => (
            <div key={uf} className="bg-blue-50/50 dark:bg-white/5 p-2 rounded-xl border border-blue-100 dark:border-white/5 text-center transition-all hover:border-blue-300">
              <span className="text-[9px] font-black text-slate-400 block uppercase tracking-tighter">{uf}</span>
              <input type="number" value={Number(aliq) || 0} onChange={e => setAliquotas({...aliquotas, [uf]: Number(e.target.value)})} className="bg-transparent border-none text-xs font-black text-[#0B1F4D] dark:text-white w-full text-center outline-none" />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

/* ───────────── ABA: REGRAS ───────────── */

function RulesTab({ regrasAtivas, setRegrasAtivas, onSave }: any) {
  const { dashboard } = useFiscalStore();
  
  const toggleRegra = async (code: string) => {
    const newState = { ...regrasAtivas, [code]: !regrasAtivas[code] };
    setRegrasAtivas(newState);
    await onSave('rules_config', newState);
  };

  const { porRegra } = dashboard?.auditSummary ?? { porRegra: {} as Record<string, number> };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between px-2">
        <h3 className={cn("font-black flex items-center gap-2", NAVY_TEXT)}><Layers className="size-5 text-blue-500" /> Motor de Inspeção</h3>
        <Badge className="bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20 uppercase text-[9px] font-black px-3 py-1 rounded-full">Execução em Tempo Real</Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {REGRAS_SISTEMA.map(r => {
          const ativa = regrasAtivas[r.code] ?? false;
          const count = porRegra?.[r.code] ?? 0;
          const isPending = r.status === "PENDENTE_BACKEND";
          
          return (
            <Card key={r.code} className={cn(
              "p-5 flex items-start justify-between gap-4 transition-all duration-300",
              CARD_STYLE,
              !ativa && "opacity-50 grayscale-[0.5]"
            )}>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[9px] font-black bg-blue-500/10 text-blue-600 px-2 py-0.5 rounded-full tracking-tight">{r.code}</span>
                  <span className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Camada {r.layer}</span>
                  {isPending && <Badge variant="outline" className="text-[8px] font-black border-amber-500/30 text-amber-600 bg-amber-500/5 px-2 rounded-full">NUVEM</Badge>}
                </div>
                <h4 className={cn("font-black text-sm", NAVY_TEXT)}>{r.title}</h4>
                <p className="text-[11px] text-slate-400 mt-1 line-clamp-2 font-medium leading-relaxed">{r.desc}</p>
                
                <div className="mt-4 flex items-center justify-between">
                  {count > 0 ? (
                    <div className="flex items-center gap-1.5 text-amber-500 text-[10px] font-black uppercase tracking-wider">
                      <div className="size-2 rounded-full bg-amber-500 animate-pulse shadow-[0_0_8px_rgba(245,158,11,0.5)]" /> 
                      {fmtNum(count)} indícios detectados
                    </div>
                  ) : (
                    <span className="text-[9px] font-black text-emerald-500 uppercase tracking-widest bg-emerald-500/5 px-2 py-0.5 rounded-full">Conforme</span>
                  )}
                </div>
              </div>
              
              <button 
                disabled={isPending}
                onClick={() => toggleRegra(r.code)} 
                className={cn(
                  "hover:scale-110 active:scale-95 transition-all mt-1",
                  isPending && "opacity-20 cursor-not-allowed"
                )}
              >
                {ativa ? (
                  <ToggleRight className="size-9 text-blue-500 drop-shadow-[0_0_8px_rgba(59,130,246,0.3)]" />
                ) : (
                  <ToggleLeft className="size-9 text-slate-300 dark:text-slate-600" />
                )}
              </button>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
